import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PLAN_TYPES } from 'src/constants/cardsEnum';
import {
  DISCOUNT_CODE_QUERY_PARAM,
  FREE_PLAN_UPSELL_DISCOUNT_CODE,
} from 'src/constants/invocationUpsell';
import { PATHS } from 'src/constants/navigateRoutes';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { getHanaProTrialStatus } from 'src/utils/trial';
import styleVariables from '../../scss/_variables.scss';

const BillingTrialBanner = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationDetails } = useOrganizationStore();
  const trialStatus = useMemo(
    () => getHanaProTrialStatus(organizationDetails),
    [organizationDetails],
  );
  const isPaymentPage = location.pathname === PATHS.payment;
  const upgradeToProPaymentPath = `${PATHS.payment}?plan=${PLAN_TYPES.PRO}&${DISCOUNT_CODE_QUERY_PARAM}=${encodeURIComponent(FREE_PLAN_UPSELL_DISCOUNT_CODE)}`;

  if (!trialStatus?.isActive || isPaymentPage) {
    return null;
  }

  const remainingDaysLabel =
    trialStatus.remainingDays === 1
      ? '1 day left'
      : `${trialStatus.remainingDays} days left`;
  const trialDetails =
    'Upgrade to the PRO plan without disrupting your existing services. Your workspace keeps its current setup, memories, connectors, and workflows.';

  return (
    <Box
      sx={{
        mb: 2,
        px: { xs: 2, md: 2.5 },
        py: { xs: 1.75, md: 2 },
        borderRadius: '18px',
        border: '1px solid rgba(240,73,35,0.28)',
        background:
          'linear-gradient(135deg, rgba(240,73,35,0.18) 0%, rgba(32,14,42,0.92) 55%, rgba(15,17,18,0.98) 100%)',
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '14px',
            background: 'rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BoltRoundedIcon sx={{ color: styleVariables.colorWhite }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: styleVariables.colorWhite,
                lineHeight: 1.3,
                fontSize: { xs: '0.95rem', md: '1rem' },
              }}
            >
              You are on a free trial of Hana PRO with {remainingDaysLabel}.
            </Typography>
            <Tooltip
              title={trialDetails}
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={4000}
            >
              <IconButton
                aria-label="More information about the Hana PRO trial"
                size="small"
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  mt: -0.25,
                  color: 'rgba(255,255,255,0.82)',
                }}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: 'rgba(255,255,255,0.82)',
              lineHeight: 1.55,
              maxWidth: '880px',
              display: { xs: 'none', md: 'block' },
            }}
          >
            {trialDetails}
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        endIcon={<OpenInNewRoundedIcon fontSize="small" />}
        onClick={() => navigate(upgradeToProPaymentPath)}
        sx={{
          flexShrink: 0,
          background: styleVariables.specialGradient,
          color: styleVariables.colorWhite,
          fontWeight: 700,
          textTransform: 'none',
          minWidth: { xs: '100%', md: 'unset' },
        }}
      >
        Upgrade to PRO
      </Button>
    </Box>
  );
});

export default BillingTrialBanner;
